/**
 * Ported from sara/app
 * `src/components/ear-reader/vocabulary-exercise/use-fluency-timer.ts` @
 * e8e8fecb4ec5.
 *
 * Bindery is canonical for this module from 2026-09-04; reconcile app-side
 * drift toward this copy. Deliberate divergences from the source:
 *
 * - `log` and `getWordSlug` are ref-synced alongside `onTimerExpired` and no
 *   longer sit in the review-timer effect's dependency array. The source
 *   tore down and re-armed the timer whenever a consumer passed an inline
 *   callback or a fresh logger object — any component re-rendering faster
 *   than `reviewDurationMs` had a timer that never fired.
 * - `FluencyTimerLogger.event` is a function-typed property over the
 *   two-literal `FluencyTimerEventName` union, not a bivariant method over
 *   `string` (the source imported the app's `Logger` type directly).
 * - `handleRecognized` refuses a null `activeIndex`, and `setActiveIndex`
 *   cancels a pending advance timer, so neither can strand the machine in
 *   "advancing" or advance past a just-reset index.
 */

import { useReducer, useCallback, useRef, useEffect } from "react";

// ---------------------------------------------------------------------------
// Constants (default values)
// ---------------------------------------------------------------------------

/**
 * Default duration (ms) before an unreviewed word is auto-marked as disfluent.
 * Can be overridden via `reviewDurationMs` option for accessibility or A/B testing.
 */
export const DEFAULT_REVIEW_DURATION_MS = 3000;

/**
 * Default brief pause (ms) after recognition before advancing to the next word.
 * Can be overridden via `advanceDelayMs` option for accessibility or A/B testing.
 */
export const DEFAULT_ADVANCE_DELAY_MS = 300;

// Legacy exports for backward compatibility
/** @deprecated Use DEFAULT_REVIEW_DURATION_MS instead */
export const REVIEW_DURATION_MS = DEFAULT_REVIEW_DURATION_MS;
/** @deprecated Use DEFAULT_ADVANCE_DELAY_MS instead */
export const ADVANCE_DELAY_MS = DEFAULT_ADVANCE_DELAY_MS;

// ---------------------------------------------------------------------------
// Logging seam
// ---------------------------------------------------------------------------

/**
 * The exact event names this hook emits. Declared as literals rather than
 * `string` so that assigning a concrete logger asks the question that
 * matters: does that logger's event-name union actually contain the events
 * this hook will hand it? A `string`-parameter seam (via method bivariance)
 * compiles against any logger and silently feeds unknown names to loggers
 * whose analytics schema never declared them.
 */
export type FluencyTimerEventName =
  | "reading.vocabulary.fluency.timer_expired"
  | "reading.vocabulary.fluency.advance";

/**
 * The only thing this hook needs from a logger. Declared structurally here
 * rather than imported, so packages/ui stays free of a logging dependency.
 * Written as a function-typed property (not a method) deliberately: property
 * parameters are checked contravariantly under `strictFunctionTypes`, so a
 * logger accepting an event-name union that lacks these two literals is a
 * compile error instead of a runtime surprise.
 */
export interface FluencyTimerLogger {
  event: (
    eventName: FluencyTimerEventName,
    data?: Record<string, unknown>,
  ) => void;
}

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

export type FluencyPhase = "active" | "expired" | "advancing";

export interface FluencyTimerState {
  phase: FluencyPhase;
  activeIndex: number | null;
}

export type FluencyTimerAction =
  | { type: "TIMER_EXPIRED" }
  | { type: "ADVANCE_START" }
  | { type: "ADVANCE_COMPLETE"; nextIndex: number | null }
  | { type: "SET_INDEX"; index: number | null };

export function fluencyTimerReducer(
  state: FluencyTimerState,
  action: FluencyTimerAction,
): FluencyTimerState {
  switch (action.type) {
    case "TIMER_EXPIRED":
      return { ...state, phase: "expired" };
    case "ADVANCE_START":
      return { ...state, phase: "advancing" };
    case "ADVANCE_COMPLETE":
      return { phase: "active", activeIndex: action.nextIndex };
    case "SET_INDEX":
      return { phase: "active", activeIndex: action.index };
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseFluencyTimerOptions {
  /** Initial active word index */
  initialIndex: number | null;
  /** Called when the review timer expires for a word */
  onTimerExpired: (index: number | null) => void;
  /** Logger instance for telemetry */
  log?: FluencyTimerLogger;
  /** Word slug resolver — returns the slug for a given index (for logging) */
  getWordSlug?: (index: number) => string | undefined;
  /**
   * Duration (ms) before an unreviewed word is auto-marked as disfluent.
   * Customize for accessibility or A/B testing. Default: 3000ms
   */
  reviewDurationMs?: number;
  /**
   * Brief pause (ms) after recognition before advancing to the next word.
   * Customize for accessibility or A/B testing. Default: 300ms
   */
  advanceDelayMs?: number;
}

export interface UseFluencyTimerReturn {
  /** Current state phase: active (timer running), expired, or advancing */
  phase: FluencyPhase;
  /** Currently active word index */
  activeIndex: number | null;
  /** Call when the user recognizes the current word — triggers auto-advance */
  handleRecognized: () => void;
  /** Manually advance to the next word (e.g. via spacebar after expiry) */
  advanceToNextWord: () => void;
  /** Directly set the active index (used by parent for initial mount) */
  setActiveIndex: (index: number | null) => void;
}

/**
 * Manages the fluency review timer state machine.
 *
 * State transitions:
 *   active → (REVIEW_DURATION_MS elapses) → expired
 *   active → (user recognizes word) → advancing → active (next word)
 *   expired → (user presses space / clicks Next) → active (next word)
 */
export function useFluencyTimer({
  initialIndex,
  onTimerExpired,
  log,
  getWordSlug,
  reviewDurationMs = DEFAULT_REVIEW_DURATION_MS,
  advanceDelayMs = DEFAULT_ADVANCE_DELAY_MS,
}: UseFluencyTimerOptions): UseFluencyTimerReturn {
  const [state, dispatch] = useReducer(fluencyTimerReducer, {
    phase: "active",
    activeIndex: initialIndex,
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep every consumer-supplied callback behind a ref so the review timer
  // is armed once per (phase, index) and never torn down by a mere render.
  // A consumer passing an inline `getWordSlug={(i) => words[i]?.slug}` or a
  // per-render logger object would otherwise re-create the effect each
  // render, and any component re-rendering faster than reviewDurationMs
  // would keep a timer that never fires.
  const onTimerExpiredRef = useRef(onTimerExpired);
  const logRef = useRef(log);
  const getWordSlugRef = useRef(getWordSlug);
  const advanceToNextWordRef = useRef<() => void>(() => {
    // Initialized when advanceToNextWord is created below
  });

  useEffect(() => {
    onTimerExpiredRef.current = onTimerExpired;
    logRef.current = log;
    getWordSlugRef.current = getWordSlug;
  }, [onTimerExpired, log, getWordSlug]);

  // --- Timer lifecycle ---

  const clearReviewTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearAdvanceTimer = useCallback(() => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    clearReviewTimer();
    clearAdvanceTimer();
  }, [clearReviewTimer, clearAdvanceTimer]);

  // Start the review timer whenever we enter the "active" phase
  useEffect(() => {
    if (state.phase !== "active" || state.activeIndex === null) return;
    const activeIndex = state.activeIndex;

    clearReviewTimer();
    timerRef.current = setTimeout(() => {
      onTimerExpiredRef.current(activeIndex);
      dispatch({ type: "TIMER_EXPIRED" });

      const slug = getWordSlugRef.current?.(activeIndex);
      logRef.current?.event("reading.vocabulary.fluency.timer_expired", {
        word_slug: slug,
        duration_ms: reviewDurationMs,
        auto_marked: true,
      });
    }, reviewDurationMs);

    return clearReviewTimer;
  }, [state.phase, state.activeIndex, clearReviewTimer, reviewDurationMs]);

  // Cleanup all timers on unmount
  useEffect(() => clearAllTimers, [clearAllTimers]);

  // --- Actions ---

  const advanceToNextWord = useCallback(() => {
    if (state.activeIndex === null) return;

    const nextIndex = state.activeIndex + 1;
    const slug = getWordSlugRef.current?.(state.activeIndex);
    logRef.current?.event("reading.vocabulary.fluency.advance", {
      from_index: state.activeIndex,
      to_index: nextIndex,
      word_slug: slug,
    });

    dispatch({ type: "ADVANCE_COMPLETE", nextIndex });
  }, [state.activeIndex]);

  // Keep advanceToNextWordRef current
  useEffect(() => {
    advanceToNextWordRef.current = advanceToNextWord;
  }, [advanceToNextWord]);

  const handleRecognized = useCallback(() => {
    // Guard on the index too: dispatching ADVANCE_START with no active word
    // would enter "advancing" with no timer to ever leave it.
    if (state.phase !== "active" || state.activeIndex === null) return;

    clearReviewTimer();
    dispatch({ type: "ADVANCE_START" });

    advanceTimerRef.current = setTimeout(() => {
      advanceToNextWordRef.current();
    }, advanceDelayMs);
  }, [state.phase, state.activeIndex, clearReviewTimer, advanceDelayMs]);

  const setActiveIndex = useCallback(
    (index: number | null) => {
      // A reset within advanceDelayMs must not let the stale advance fire
      // and step past the index being set.
      clearAdvanceTimer();
      dispatch({ type: "SET_INDEX", index });
    },
    [clearAdvanceTimer],
  );

  return {
    phase: state.phase,
    activeIndex: state.activeIndex,
    handleRecognized,
    advanceToNextWord,
    setActiveIndex,
  };
}
